import { useEffect, useRef, useState } from 'react';

const SilenceDurationMs = 800;
const MinRecordingMs = 300;
const MaxRecordingMs = 45000;

function run_cleanup(refs, set_is_speaking) {
	if(refs.animation_frame_ref.current) {
		cancelAnimationFrame(refs.animation_frame_ref.current);
		refs.animation_frame_ref.current = null;
	}
	if(refs.silence_timer_ref.current) {
		clearTimeout(refs.silence_timer_ref.current);
		refs.silence_timer_ref.current = null;
	}
	if(refs.max_duration_timer_ref.current) {
		clearTimeout(refs.max_duration_timer_ref.current);
		refs.max_duration_timer_ref.current = null;
	}
	if(refs.media_recorder_ref.current && refs.is_recording_ref.current) {
		refs.media_recorder_ref.current.stop();
	}
	if(refs.stream_ref.current) {
		refs.stream_ref.current.getTracks().forEach(function(track) {
			track.stop();
		});
		refs.stream_ref.current = null;
	}
	if(refs.audio_context_ref.current) {
		refs.audio_context_ref.current.close();
		refs.audio_context_ref.current = null;
	}
	refs.analyser_ref.current = null;
	refs.is_recording_ref.current = false;
	set_is_speaking(false);
}

function start_recording(refs, set_is_speaking) {
	if(refs.is_recording_ref.current || !refs.stream_ref.current) {
		return;
	}

	let mime_type = 'audio/webm';
	if(MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
		mime_type = 'audio/webm;codecs=opus';
	}

	refs.audio_chunks_ref.current = [];

	const media_recorder = new MediaRecorder(refs.stream_ref.current, { mimeType: mime_type });

	media_recorder.ondataavailable = function(event) {
		if(event.data.size > 0) {
			refs.audio_chunks_ref.current.push(event.data);
		}
	};

	media_recorder.onstop = function() {
		const blob = new Blob(refs.audio_chunks_ref.current, { type: 'audio/webm' });
		blob.arrayBuffer().then(function(buffer) {
			if(refs.on_chunk_ready_ref.current) {
				refs.on_chunk_ready_ref.current(buffer);
			}
		});
		refs.audio_chunks_ref.current = [];
		refs.is_recording_ref.current = false;
	};

	refs.media_recorder_ref.current = media_recorder;
	media_recorder.start();
	refs.is_recording_ref.current = true;
	refs.recording_start_ref.current = Date.now();
	set_is_speaking(true);

	refs.max_duration_timer_ref.current = setTimeout(function() {
		stop_recording(refs, set_is_speaking);
		refs.max_duration_timer_ref.current = null;
	}, MaxRecordingMs);
}

function stop_recording(refs, set_is_speaking) {
	if(!refs.is_recording_ref.current || !refs.media_recorder_ref.current) {
		return;
	}
	const duration = Date.now() - refs.recording_start_ref.current;
	if(duration < MinRecordingMs) {
		return;
	}
	if(refs.max_duration_timer_ref.current) {
		clearTimeout(refs.max_duration_timer_ref.current);
		refs.max_duration_timer_ref.current = null;
	}
	refs.media_recorder_ref.current.stop();
	set_is_speaking(false);
}

function tick(refs, data_array, set_is_speaking) {
	refs.analyser_ref.current.getByteTimeDomainData(data_array);

	let sum_squares = 0;
	for (let index = 0; index < data_array.length; index++) {
		const normalized = (data_array[index] / 128.0) - 1.0;
		sum_squares = sum_squares + (normalized * normalized);
	}
	const rms = Math.sqrt(sum_squares / data_array.length);

	const current_speech_threshold = refs.speech_threshold_ref.current;
	const current_silence_threshold = current_speech_threshold * 0.6;

	if(rms > current_speech_threshold) {
		if(refs.silence_timer_ref.current) {
			clearTimeout(refs.silence_timer_ref.current);
			refs.silence_timer_ref.current = null;
		}
		if(!refs.is_recording_ref.current) {
			start_recording(refs, set_is_speaking);
		}
	} else if(rms < current_silence_threshold && refs.is_recording_ref.current) {
		if(!refs.silence_timer_ref.current) {
			refs.silence_timer_ref.current = setTimeout(function() {
				stop_recording(refs, set_is_speaking);
				refs.silence_timer_ref.current = null;
			}, SilenceDurationMs);
		}
	}

	refs.animation_frame_ref.current = requestAnimationFrame(tick.bind(null, refs, data_array, set_is_speaking));
}

function start_monitoring(refs, set_is_speaking) {
	const analyser = refs.analyser_ref.current;
	if(!analyser) {
		return;
	}
	const data_array = new Uint8Array(analyser.fftSize);
	refs.animation_frame_ref.current = requestAnimationFrame(tick.bind(null, refs, data_array, set_is_speaking));
}

function useAudioRecorder(is_listening, on_chunk_ready, speech_threshold) {
	const speech_threshold_ref = useRef(speech_threshold || 0.025);
	const audio_context_ref = useRef(null);
	const analyser_ref = useRef(null);
	const media_recorder_ref = useRef(null);
	const stream_ref = useRef(null);
	const animation_frame_ref = useRef(null);
	const silence_timer_ref = useRef(null);
	const max_duration_timer_ref = useRef(null);
	const recording_start_ref = useRef(null);
	const audio_chunks_ref = useRef([]);
	const is_recording_ref = useRef(false);
	const on_chunk_ready_ref = useRef(on_chunk_ready);
	const [is_speaking, set_is_speaking] = useState(false);

	const refs = {
		speech_threshold_ref,
		audio_context_ref,
		analyser_ref,
		media_recorder_ref,
		stream_ref,
		animation_frame_ref,
		silence_timer_ref,
		max_duration_timer_ref,
		recording_start_ref,
		audio_chunks_ref,
		is_recording_ref,
		on_chunk_ready_ref
	};

	useEffect(() => {
		on_chunk_ready_ref.current = on_chunk_ready;
	}, [on_chunk_ready]);

	useEffect(() => {
		speech_threshold_ref.current = speech_threshold || 0.025;
	}, [speech_threshold]);

	useEffect(() => {
		if(!is_listening) {
			run_cleanup(refs, set_is_speaking);
			return;
		}

		navigator.mediaDevices.getUserMedia({ audio: true })
			.then(function(stream) {
				stream_ref.current = stream;

				const audio_context = new AudioContext();
				audio_context_ref.current = audio_context;
				audio_context.resume();

				const source = audio_context.createMediaStreamSource(stream);
				const analyser = audio_context.createAnalyser();
				analyser.fftSize = 256;
				source.connect(analyser);
				analyser_ref.current = analyser;

				start_monitoring(refs, set_is_speaking);
			})
			.catch(function(error) {
				console.error('Microphone access error: ' + error.message);
			});

		return function() {
			run_cleanup(refs, set_is_speaking);
		};
	}, [is_listening]);

	return { analyser_ref: analyser_ref, is_speaking: is_speaking };
}

export default useAudioRecorder;
